import type { ApiSuccessResponse, ApiErrorResponse } from '../../lib/apiHandler';

// DirectFlow API endpoint
// Routed through the Vite dev proxy in development — the admin API sends no
// CORS headers for localhost. See vite.config.ts and adminApi() in lib/api.ts.
const DIRECTFLOW_API_URL = import.meta.env.DEV
  ? '/admin-api/api/contact'
  : 'https://admin.theviewshed.com/api/contact';

// Interface for the expected contact form data
export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/**
 * API endpoint for sending contact form emails
 */
export async function sendContactEmail(formData: ContactFormData): Promise<ApiSuccessResponse | ApiErrorResponse> {
  try {
    const { name, email, subject, message } = formData;
    
    // Validate the form data
    if (!name || !email || !subject || !message) {
      return { 
        success: false as const, 
        error: 'All fields are required' 
      };
    }
    
    // Forward the submission to DirectFlow API
    const directFlowResponse = await fetch(DIRECTFLOW_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        email,
        subject,
        message
      })
    });
    
    // Check if the request was successful
    if (!directFlowResponse.ok) {
      console.error('DirectFlow API error:', directFlowResponse.statusText);
      return { 
        success: false as const, 
        error: `Failed to send message: ${directFlowResponse.statusText}` 
      };
    }
    
    // Attempt to parse the response
    try {
      const responseData = await directFlowResponse.json();
      return { 
        success: true as const, 
        data: responseData 
      };
    } catch (parseError) {
      // If we can't parse the response but the request was successful, still return success
    return { 
      success: true as const, 
        data: { message: 'Message sent successfully' } 
    };
    }
  } catch (error) {
    console.error('Exception sending message:', error);
    return { 
      success: false as const, 
      error: error instanceof Error ? error.message : 'An unknown error occurred' 
    };
  }
} 